import { createContext, ReactElement, ReactNode, useContext, useMemo } from 'react'

import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import { useFetchExternalInvoiceFromBilly } from '@features/invoices/hooks/useFetchExternalInvoiceFromBilly'
import { useFetchInvoice } from '@features/invoices/hooks/useFetchInvoice'
import { ExternalInvoiceFromBilly } from '@features/invoices/types/externalInvoiceFromBilly'
import { Invoice } from '@features/invoices/types/invoices/invoice'

import { useInvoiceShouldRefetch } from '../hooks/useInvoiceShouldRefetch'

interface ContextState {
  billyApiExternalInvoice?: ExternalInvoiceFromBilly
  billyApiExternalInvoiceLoading: boolean
  documentType: InvoiceDocumentType
  invoice?: Invoice
  invoiceId: string
  isFetching: boolean
  isLoading: boolean
}

const InvoiceContext = createContext<ContextState | undefined>(undefined)

interface InvoiceContextProps {
  children?: ReactNode
  documentType: InvoiceDocumentType
  invoiceId: string
}

export const InvoiceContextProvider = ({ children, documentType, invoiceId }: InvoiceContextProps): ReactElement => {
  const getInvoiceRefetchInterval = useInvoiceShouldRefetch(documentType)
  const { invoice, isLoading, isFetching } = useFetchInvoice(documentType, invoiceId, getInvoiceRefetchInterval)

  const idToFetch = invoice?.externalInvoiceId || invoice?.externalCreditNoteId || ''
  const { externalInvoice: billyApiExternalInvoice, isLoading: billyApiExternalInvoiceLoading } =
    useFetchExternalInvoiceFromBilly(idToFetch)

  const contextValue = useMemo(
    () => ({
      billyApiExternalInvoice,
      billyApiExternalInvoiceLoading,
      documentType,
      invoice,
      invoiceId,
      isFetching,
      isLoading,
    }),
    [billyApiExternalInvoice, billyApiExternalInvoiceLoading, documentType, invoice, invoiceId, isFetching, isLoading],
  )

  return <InvoiceContext.Provider value={contextValue}>{children}</InvoiceContext.Provider>
}

export const useInvoice = () => {
  const context = useContext(InvoiceContext)

  if (!context) {
    throw new Error('InvoiceContextProvider is missing in the module!')
  }

  return context
}
